import {
  useCreateSupportTicket,
  useMarkSupportRead,
  useSendSupportMessage,
  useSupportMessages,
  useSupportTickets,
} from "@features/support/api";
import { extractErrorMessage } from "@shared/extractErrorMessage";
import { resolveDrawerSize } from "@shared/ui/enterprise-drawer/drawerSize";
import { useStoredUser } from "@shared/useStoredUser";
import { App as AntdApp, Drawer, Empty, Flex, Spin } from "antd";
import { useCallback, useEffect, useMemo, useRef } from "react";
import SupportMessageInput from "./SupportMessageInput";
import SupportMessageThread from "./SupportMessageThread";

interface Props {
  open: boolean;
  onClose: () => void;
}

const SupportTicketDrawer = ({ open, onClose }: Props) => {
  const user = useStoredUser();
  const { message } = AntdApp.useApp();

  const { data: tickets, isLoading: ticketsLoading } = useSupportTickets(
    open ? { created_by: user?.id } : undefined,
  );

  const activeTicket = useMemo(
    () => tickets?.find((t) => t.status === "new" || t.status === "accepted"),
    [tickets],
  );

  const { data: messages, isLoading: messagesLoading } = useSupportMessages(activeTicket?.id);

  const createTicket = useCreateSupportTicket();
  const sendMessage = useSendSupportMessage();
  const { mutate: markAsRead } = useMarkSupportRead();
  const markedRef = useRef<string | undefined>(undefined);

  const handleSend = useCallback(
    (text: string) => {
      const doSend = (ticketId: string) => {
        sendMessage.mutate(
          { id: ticketId, data: { body: text } },
          { onError: (err: unknown) => message.error(extractErrorMessage(err)) },
        );
      };

      if (activeTicket?.id) {
        doSend(activeTicket.id);
        return;
      }

      createTicket.mutate(
        { data: {} },
        {
          onSuccess: (resp) => {
            const id = resp?.id;
            if (id) {
              doSend(id);
            } else {
              message.error("Ошибка создания обращения");
            }
          },
          onError: (err: unknown) => message.error(extractErrorMessage(err)),
        },
      );
    },
    [activeTicket?.id, createTicket, sendMessage, message],
  );

  // Auto-scroll when new messages arrive
  useEffect(() => {
    if (!open || !messages?.length) return;
    const el = document.getElementById("support-thread-end");
    el?.scrollIntoView({ behavior: "smooth" });
  }, [open, messages]);

  // Auto-mark read once when opening ticket
  useEffect(() => {
    if (!open || !activeTicket?.id) return;
    if (markedRef.current === activeTicket.id) return;
    markedRef.current = activeTicket.id;
    markAsRead({ id: activeTicket.id });
  }, [open, activeTicket?.id, markAsRead]);

  useEffect(() => {
    if (!open) markedRef.current = undefined;
  }, [open]);

  const loading = ticketsLoading || messagesLoading;
  const sending = sendMessage.isPending || createTicket.isPending;

  return (
    <Drawer
      title="Поддержка"
      open={open}
      onClose={onClose}
      size={resolveDrawerSize(420)}
      styles={{ body: { display: "flex", flexDirection: "column", padding: "8px 16px" } }}
    >
      <Flex vertical flex={1} style={{ overflow: "auto", minHeight: 0 }}>
        {loading ? (
          <Flex align="center" justify="center" flex={1}>
            <Spin />
          </Flex>
        ) : !activeTicket ? (
          <Empty description="Нет активных обращений. Напишите сообщение, чтобы создать обращение." />
        ) : (
          <>
            <SupportMessageThread messages={messages ?? []} ticketId={activeTicket?.id} />
            <div id="support-thread-end" />
          </>
        )}
      </Flex>
      <div style={{ paddingTop: 8 }}>
        <SupportMessageInput onSend={handleSend} loading={sending} />
      </div>
    </Drawer>
  );
};

export default SupportTicketDrawer;
